import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { faCalendarAlt, faFile } from '@fortawesome/free-regular-svg-icons';
import { faCheckCircle, faEllipsisV, faExclamationCircle, faFileSignature, faHourglassHalf, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Divider, List, ListItem, ListItemText, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, withStyles } from '@material-ui/core';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { default as classnames, default as classNames } from 'classnames';
import { Filter, Loader, SGSButton, SGSCard, SGSColumnFilter, SGSHelpButton, SGSTab, SGSTableCtrl, SGSTabPanel, SGSTabs, SGSTextButton, SGSTooltip } from 'components';
import SGSDrawer from 'components/SGSDrawer';
import { useAppContext } from 'core/contexts';
import { useRadiationNotifsContext } from 'core/contexts/RadiationProvider';
import { Affiliated, initAffiliated, RadiationNotif } from 'core/models';
import { exportAffiliates } from 'core/services/affilie';
import { enumStatusCommand } from 'core/services/statusCommand';
import { mercure } from 'core/utils';
import { hasRole } from 'core/utils/roles';
import { useQueryConf } from 'core/utils/useQuery';
import { getRisk } from 'core/utils/utils';
import { format } from 'date-fns';
import { filtersChanged, INITIAL_SEARCH_PARAMS, pageChanged, pageSizeChanged, resetSearchParams, sortByChanged } from 'features/affiliates/searchParamsSlice';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Cell, ColumnInterface } from 'react-table';
import { affilieService, menusService, statusCommand } from '../../../core/services';
import styles from './Affiliates.module.scss';

import SGSModal from 'components/SGSModal';
import { useNotificationContext } from 'features/notification/contexts/NotificationProvider';
import PQueue from 'p-queue/dist';

const queue = new PQueue({ concurrency: 1 });

type Props = {};

const Affiliates: React.FC<Props> = () => {
    const history = useHistory();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { user } = useAppContext();
    const { addNotification } = useNotificationContext();
    const { isOnGoingCancellation, addRadiationNotif, removeRadiationNotif, isRadiationNotifInList, setRadiationNewEvent, radiationNotifs, radiationNewEvent } = useRadiationNotifsContext();
    let searchParams = useSelector((state: any) => state.searchParams.value);
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedAffiliated, setSelectedAffiliated] = useState<Affiliated>(initAffiliated);
    const [selectedTab, setSelectedTab] = useState(0);
    const [downloading, setDownloading] = useState(false);
    const [contratsLoading, setContratsLoading] = useState(false);
    const [contrats, setContrats] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [souscripChoise, setSouscripChoise] = useState([]);

    // Load menu data
    const { isLoading: isLoadingCSP, data: dataCSP } = useQuery([user.login, 'CSP'], async () => await menusService.getMenuCSP(), useQueryConf);
    const { isLoading: isLoadingSouscripteurs, data: dataSouscripteurs } = useQuery([user.login, 'souscripteurs'], async () => await menusService.getMenuSouscripteurs(), useQueryConf);
    const { isLoading: isLoadingTypesContrat, data: dataTypesContrat } = useQuery([user.login, 'typesContrat'], async () => await menusService.getMenuTypesContrat(), useQueryConf);
    const { isLoading: isLoadingStatutsAdhesion, data: dataStatutsAdhesion } = useQuery([user.login, 'statutsAdhesion'], async () => await menusService.getMenuStatutsAdhesion(), useQueryConf);

    /**
     * Handles the change of filter value.
     * Resets the page to 1 and sets the new filter.
     *
     * @param {string} value - The new filter value.
     */
    const handleChangeFilter = (value) => {
        dispatch(pageChanged(1));
        setActiveFilters(value);
    };

    // Reset searchParam
    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const keepNav = queryParams.get('noreset');
        if (!keepNav) {
            searchParams = JSON.stringify(INITIAL_SEARCH_PARAMS);
            setActiveFilters([]);
            dispatch(resetSearchParams());
        }
        const search = queryParams.get('search');
        if (search) {
            handleChangeFilter([{ nom: search }]);
        }
    }, []);

    // Load affilies
    const {
        isLoading: isLoadingAffiliates,
        isFetching,
        data: dataAffiliates,
    } = useQuery(
        [user.login, 'affiliates', searchParams],
        async () => {
            const { page, pageLimit, sortBy, search } = JSON.parse(searchParams);
            const sortPayload = sortBy.length ? createSortPayload(sortBy[0].id, sortBy[0].desc) : [];
            return await affilieService.getAffilies(page, pageLimit, sortPayload, search);
        },
        { ...useQueryConf, cacheTime: 0 },
    );

    const affiliates = dataAffiliates?.data ?? [];
    const totalPages = dataAffiliates?.totalPage ?? 0;
    const totalResults = dataAffiliates?.totalResultat ?? 0;

    /**
     * Fetches the list of on-going cancellations for affiliates (status = 5)
     */
    useEffect(() => {
        if (dataAffiliates?.data?.length) {
            const keyList = dataAffiliates.data.map((a) => `${a.codAffilie}-${a.codSouscrip}`);
            getOnGoingCancelAffilie(keyList);
        }
    }, [dataAffiliates]);

    const isLoadingData = isLoadingAffiliates || isFetching;

    /**
     * Create a Mercure listener for the specified command ID to remove notification
     * and update affilie data in list and modal
     * @param {string} id - The ID of the Mercure event stream.
     */
    const createMercureListener = (id) => {
        const mercureFunction = async (event) => {
            (async () => {
                await queue.add(() => {
                    // Remove notification
                    removeRadiationNotif(id);
                    // update affilie data in list
                    setRadiationNewEvent(event.result);
                });
            })();
            if (event?.result?.hasError) {
                addNotification({ commandId: event.id, content: event.result, type: enumStatusCommand.CANCEL_AFFILIE });
            }
        };

        const eventSource = mercure.createListener(id, mercureFunction);
        return eventSource;
    };

    const queryClient = useQueryClient();
    const updateCachedDataforAffiliates = async (codAffilie, codSouscrip, newData) => {
        const queryCache = queryClient.getQueryCache();
        const cachedQueries = queryCache.findAll();
        const cacheKeys = cachedQueries.map((query) => query.queryKey);
        const affiliatesCachedKey = cacheKeys.filter((k) => k.includes('affiliates'));

        // Defined new status
        let status = newData.subscriptions.map((d) => d.status);
        let maxStatus = Math.max(...status).toString();
        let listStatus = newData.hasError ? 'Erreur lors de la radiation' : maxStatus ? 'Adhésion pré-fermée' : 'Adhésion fermée';

        // Set status has error
        if (newData.hasError) maxStatus = '7';

        for (const cacheKey of affiliatesCachedKey) {
            const cachedData: any = await queryClient.getQueryData(cacheKey);
            if (cachedData && Array.isArray(cachedData.data)) {
                const element = cachedData.data.find((el) => el.codAffilie === codAffilie && el.codSouscrip === codSouscrip);
                if (element) {
                    element.status = maxStatus;
                    element.lbStatut = listStatus;
                }
                // update cache and re-render
                queryClient.setQueryData(cacheKey, (element) => {
                    return element;
                });
            }
        }

        return null;
    };

    /**
     * Update affiliate data based on the souscrip data.
     * @param {Object} data - The data object containing affiliate and souscrip data.
     */
    const updateAffiliateDataBySouscrip = (data) => {
        /**
         * Asynchronously update affiliate data
         */
        const updateAffiliateData = async () => {
            // Get last API call
            const cacheKey = [user.login, 'affiliates', searchParams];
            const cachedData = await queryClient.getQueryData(cacheKey);

            if (cachedData) {
                // If cached data exists, update the cached data for affiliates
                updateCachedDataforAffiliates(data.affilie.codAffilie, data.souscrip.codSouscrip, data);
                // Reset
                setRadiationNewEvent(null);
            } else {
                // If cached data doesn't exist (api call is loading), retry after a delay
                setTimeout(updateAffiliateData, 200);
            }
        };

        // Start the affiliate data update process
        updateAffiliateData();
    };

    /**
     * Update subscription in modal
     */
    const updateSubscription = (contrats, radiationNewEvent) => {
        if (radiationNewEvent) {
            const codAffilieInContrats = Array.from(new Set(contrats.map((c) => c.cod)))[0];
            const codAffilie = radiationNewEvent.affilie.codAffilie;
            if (codAffilieInContrats === codAffilie) {
                contrats.forEach((contrat) => {
                    const sub = radiationNewEvent.subscriptions.find((c) => (c.codContrat = contrat.codContrat));
                    if (sub && sub.etat === 'Valide') {
                        const dateStr = sub.dateFinAdhesion;
                        const utcDateTime = new Date(dateStr + 'T00:00:00Z').toISOString();
                        const result = {
                            date: utcDateTime,
                            timezone_type: 3,
                            timezone: 'UTC',
                        };

                        contrat.codStatut = sub.codStatutAdh;
                        contrat.statut = sub.statutAdh;
                        contrat.dtFin = result;
                    }
                });

                setContrats(contrats);
            }
        }
    };

    useEffect(() => {
        if (radiationNewEvent) {
            updateAffiliateDataBySouscrip({ ...radiationNewEvent });
            if (contrats && drawerOpened) updateSubscription(contrats, radiationNewEvent);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [radiationNewEvent]);

    const handleSelectedTab = (_event: React.ChangeEvent<{}>, newValue: number) => {
        setSelectedTab(newValue);
    };
    const handleDetails = (row: any) => {
        toggleDrawer(true);
        setSelectedTab(0);
        setSelectedAffiliated({ ...row.original });
        setContratsLoading(true);
        affilieService
            .getAffilie(row.original.numSecu)
            .then(async (result) => {
                setContrats(result);
                // Get unique key pairs (codAffiliate + CodSouscrip)
                const keyList = Array.from(new Set(result.map((o) => `${o.cod}-${o.souscripteurCod}`)));
                await getOnGoingCancelAffilie(keyList);
            })
            .finally(() => setContratsLoading(false));
    };

    const handleExport = () => {
        setDownloading(true);
        exportAffiliates(activeFilters)
            .then((data) => {
                const url = window.URL.createObjectURL(
                    new Blob([data as BlobPart], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    }),
                );
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `affilies.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
            .finally(() => {
                setDownloading(false);
            });
    };

    const columns = useMemo<ColumnInterface<Affiliated>[]>(
        () => [
            {
                id: 'status',
                Header: '',
                headerClassName: styles.tableBorderRight__header,
                className: `${styles.tableBorderRight__cell} justify-content-center text-center`,
                accessor: (row: Affiliated) => `${row.status} ${row.lbStatut}`,
                minWidth: 50,
                maxWidth: 50,
                disableFilters: true,
                Cell: ({ row: { original: row } }: Cell<Affiliated>) => {
                    return (
                        <SGSTooltip className="text-center" title={<Box>{row.lbStatut}</Box>}>
                            <Box>{getStatusIcon(row.status)}</Box>
                        </SGSTooltip>
                    );
                },
                options: {
                    display: false,
                },
            },
            {
                Header: t('affiliations.affiliates.namesAndNIR'),
                accessor: (row: Affiliated) => `${row.nom} ${row.prenom} ${row.numSecu}`,
                id: 'nom', // Required because our accessor is a function
                minWidth: 200,
                disableFilters: true,
                headerClassName: styles.tableBorderRight__header,
                className: `${styles.tableBorderRight__cell}`,
                Cell: (props: any) => {
                    const { rows, row } = props;
                    if (row.index === 0 || rows[row.index - 1].cells[1].value !== rows[row.index].cells[1].value) {
                        return (
                            <Box>
                                <Box className={styles.strong}>
                                    {row.original.nom} {row.original.prenom}
                                </Box>
                                <Box>{row.original.numSecu}</Box>
                            </Box>
                        );
                    }

                    return null;
                },
            },
            {
                Header: t('affiliations.affiliates.matricule'),
                headerClassName: styles.tableBorderRight__header,
                className: `${styles.tableBorderRight__cell} justify-content-center text-center`,
                accessor: 'matriculeEntreprise',
                maxWidth: 100,
                disableFilters: true,
            },
            {
                Header: t('affiliations.affiliates.csp'),
                headerClassName: styles.tableBorderRight__header,
                className: `${styles.tableBorderRight__cell} justify-content-center text-center`,
                accessor: 'categorie',
                minWidth: 150,
                disableFilters: true,
            },
            {
                Header: t('affiliations.affiliates.society'),
                headerClassName: styles.tableBorderRight__header,
                className: `${styles.tableBorderRight__cell}`,
                accessor: ((row) => `${row.nomSouscripteur} ${row.siret}`) as any,
                id: 'nomSouscripteur',
                minWidth: 200,
                disableFilters: true,
                Cell: (props: any) => {
                    const { rows, row } = props;
                    if (row.index === 0 || rows[row.index - 1].cells[1].value !== rows[row.index].cells[1].value) {
                        return (
                            <Box>
                                <Box className={styles.strong}>{row.original.nomSouscripteur}</Box>
                                <Box>{row.original.siret}</Box>
                            </Box>
                        );
                    }

                    return null;
                },
            },
            {
                Header: t('affiliations.affiliates.hireDate'),
                headerClassName: styles.tableBorderRight__header,
                className: `justify-content-center text-center`,
                id: 'dtEmbauche',
                accessor: ((row) => (row?.dtEmbauche ? moment(row.dtEmbauche, 'YYYY-MM-DD').format('DD/MM/YYYY') : '')) as any,
                maxWidth: 80,
                disableFilters: true,
            },
            {
                Header: '',
                accessor: 'none' as any,
                className: 'p-0 align-items-stretch',
                disableFilters: true,
                disableSortBy: true,
                minWidth: 25,
                maxWidth: 25,
                Cell: ({ row }: Cell<Affiliated>) => {
                    return (
                        <div className={styles.actions}>
                            <SGSTooltip title={<Box>{t('details')}</Box>}>
                                <div className={styles.actions__menu} onClick={() => handleDetails(row)}>
                                    <FontAwesomeIcon icon={faEllipsisV} size="lg" />
                                </div>
                            </SGSTooltip>
                        </div>
                    );
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t],
    );

    const filterableColumnLabels = {
        nom: { value: t('affiliations.affiliates.name'), type: 'input' },
        prenom: { value: t('affiliations.affiliates.firstname'), type: 'input' },
        numSecu: { value: t('affiliations.affiliates.NIR'), type: 'input' },
        matriculeEntreprise: { value: t('affiliations.affiliates.matricule'), type: 'input' },
        categorie: {
            value: t('affiliations.affiliates.csp'),
            type: 'autocomplete',
            options: dataCSP || [],
            isLoading: isLoadingCSP,
        },
        nomSouscripteur: {
            value: t('affiliations.affiliates.societyName'),
            type: 'autocomplete',
            options: dataSouscripteurs || [],
            isLoading: isLoadingSouscripteurs,
        },
        siret: { value: t('affiliations.affiliates.siret'), type: 'input' },
        typeContrat: {
            value: 'Type de contrat',
            type: 'autocomplete',
            options: dataTypesContrat || [],
            isLoading: isLoadingTypesContrat,
        },
        lbStatut: {
            value: "Statut d'adhésion",
            type: 'autocomplete',
            options: dataStatutsAdhesion || [],
            isLoading: isLoadingStatutsAdhesion,
        },
    };

    const parsedParams = JSON.parse(searchParams);
    const [activeFilters, setActiveFilters] = useState<Filter[]>(parsedParams.search ?? []);

    const methods = useForm({
        defaultValues: {},
    });

    /**
     * Get a list of contracts that can be cancelled.
     * Excludes contracts with specific statuses and are not already in the process of cancellation.
     *
     * @param {string} codAffilie - The affiliate code to check for cancellable contracts.
     * @returns {Array} - List of cancellable contracts.
     */
    const getCancellableContracts = (codAffilie) => {
        // Excluded contract statuses
        const excludedContractStatus = ['ADHPREFERMEE', 'ADHFERMEE', 'ANISTE'];

        // Get cancelled contracts in treatment for current affiliate
        const onGoingCancelled = radiationNotifs.length
            ? radiationNotifs.filter((r) => r.data.codAffilie === codAffilie).flatMap((c) => c.data?.contracts?.map((contract) => contract.idContract) || [])
            : [];

        // Get cancellable contracts
        return getActiveContrats(contrats).filter((c) => !excludedContractStatus.includes(c.codStatut) && !onGoingCancelled.includes(c.codContrat));
    };

    /**
     * Check if there is only one subscriber in the list of cancellable contracts for the affiliate.
     * If there is only one subscriber, navigate to the cancellation form. Otherwise, open the subscriber selection modal.
     *
     * @param {string} codAffilie - The affiliate code.
     */
    const handleCancelAffilie = (codAffilie) => {
        const cancellableContracts = getCancellableContracts(codAffilie);
        const souscrip: { souscripteurCod: string; souscrip: string }[] = Array.from(
            new Map(getActiveContrats(cancellableContracts).map((c) => [c.souscripteurCod, { souscripteurCod: c.souscripteurCod, souscrip: c.nomSouscripteur }])).values(),
        );
        setSouscripChoise(souscrip);
        if (souscrip.length === 1) {
            history.push({
                pathname: `radiations/${selectedAffiliated.codAffilie}/${souscrip[0].souscripteurCod}`,
            });
        } else {
            // Open souscrip selection modal
            setIsModalOpen(true);
        }
    };

    /**
     * Determines whether the cancel button should be displayed.
     * The button will be displayed if the user is an admin or has cancel affilie role,
     * and there are active contracts available for cancellation.
     * Active contracts are those that do not have excluded contract statuses
     * and are not already in the process of cancellation.
     *
     * @param {string} codAffilie - The affiliate code to check for cancellable contracts.
     * @returns {boolean} True if the cancel button should be displayed, otherwise false.
     */
    const shouldDisplayCancelButton = (codAffilie) => {
        // Check user roles
        const isAdmin = hasRole(user, ['ROLE_ADMIN']);
        const hasRoleCancelAffilie = hasRole(user, ['ROLE_RADIATION']);
        const cancellableContracts = getCancellableContracts(codAffilie);
        return (isAdmin || hasRoleCancelAffilie) && cancellableContracts.length > 0;
    };

    /**
     * Get ongoing commands with status "ValidateBiaCommand"
     * @param keyList - array of souscrip + affiliate
     */
    const getOnGoingCancelAffilie = useCallback(async (keyList: any) => {
        if (keyList.length) {
            await statusCommand.getCommand(keyList, [enumStatusCommand.CANCEL_AFFILIE]).then((response2: any) => {
                const commandList: RadiationNotif[] = [];
                if (response2[enumStatusCommand.CANCEL_AFFILIE]) {
                    // For each ongoing command, add it to the commandList and create a mercure listener for it
                    response2[enumStatusCommand.CANCEL_AFFILIE].forEach((c) => {
                        const command = JSON.parse(c.data);
                        const radiationNotif: RadiationNotif = { id: c.id, data: command };
                        if (!isRadiationNotifInList(radiationNotif)) {
                            commandList.push(radiationNotif);
                            createMercureListener(c.id);
                        }
                    });
                }
                addRadiationNotif(commandList);
            });
        }
    }, []);

    const toggleDrawer = (open: boolean) => {
        setDrawerOpened(open);
    };

    /**
     * Handles the selection of a subscriber.
     * Navigates to the cancellation form for the selected subscriber.
     *
     * @param {string} codSouscrip - The code of the selected subscriber.
     */
    const handleSouscripSelect = (codSouscrip) => {
        history.push({
            pathname: `radiations/${selectedAffiliated.codAffilie}/${codSouscrip}`,
        });
    };

    return (
        <>
            <SGSHelpButton />
            <Box>
                <Box display="flex" className="my-2" justifyContent="left" alignItems="center">
                    <FormProvider {...methods}>
                        <SGSColumnFilter
                            columns={filterableColumnLabels}
                            className="mr-5"
                            filters={activeFilters}
                            onFiltersChange={handleChangeFilter}
                            //disabled={isLoadingData}
                            customLabel={t('filters.on*')}
                        />
                    </FormProvider>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                    <Box sx={{ flex: '1 1 auto' }}>
                        <Typography className={styles.filtersLegend}>
                            *Les filtres se basent sur les informations recensées lors de l’adhésion. Les menus déroulants vous aideront à faire votre choix en fonction de ces éléments.
                        </Typography>
                    </Box>
                    <Box className={classNames(styles.boxTotalResults)}>
                        <Typography className={styles.totalResults}>
                            {totalResults && totalResults >= 2 ? new Intl.NumberFormat('fr-FR').format(totalResults) + ' résultats' : ''}
                            {totalResults && totalResults <= 1 ? new Intl.NumberFormat('fr-FR').format(totalResults) + ' résultat' : ''}
                        </Typography>
                    </Box>
                    <SGSButton color="primary" onClick={() => handleExport()} icon="far fa-file-excel fa-lg" className={styles.export} loading={downloading}>
                        {t('global.export')}
                    </SGSButton>
                </Box>

                <SGSCard color="default" className="w-100" contentStyle={styles.cardContent}>
                    <SGSTableCtrl
                        onRowClick={handleDetails}
                        columns={columns}
                        data={affiliates}
                        loading={isLoadingData}
                        pageCount={totalPages}
                        globalFilter={activeFilters}
                        totalResults={totalResults}
                        filtersChanged={filtersChanged}
                        pageChanged={pageChanged}
                        pageSizeChanged={pageSizeChanged}
                        sortByChanged={sortByChanged}
                        searchParams={useSelector((state: any) => state.searchParams.value)}
                    ></SGSTableCtrl>
                </SGSCard>
                <SGSDrawer open={drawerOpened} toggleDrawer={toggleDrawer}>
                    {!contratsLoading && (
                        <>
                            <Box className={styles.cancel}>
                                {shouldDisplayCancelButton(selectedAffiliated.codAffilie) && (
                                    <Box className="text-right mr-5">
                                        <SGSTextButton variant="outlined" color="orange" onClick={() => handleCancelAffilie(selectedAffiliated.codAffilie)}>
                                            {/* disabled={isVisitor(user)} */}
                                            <FontAwesomeIcon icon={faTimesCircle} /> RADIER LE SALARIÉ
                                        </SGSTextButton>
                                    </Box>
                                )}
                            </Box>
                            <Box className={styles.summary}>
                                <Box className={styles.summary__top}>
                                    <Box display="flex" flexDirection="column" maxWidth="500px">
                                        {selectedAffiliated.numSecu && (
                                            <Box>
                                                {t('affiliations.affiliates.NIRNumber', {
                                                    nir: selectedAffiliated.numSecu,
                                                })}
                                            </Box>
                                        )}
                                        <Box display="flex" flexDirection="row" alignItems="center" className="mb-1">
                                            <Typography variant="h3">
                                                {selectedAffiliated.prenom} {selectedAffiliated.nom}
                                            </Typography>
                                        </Box>
                                        <Divider className={styles.divider}></Divider>
                                        {selectedAffiliated.sexe === 'F' && <Box className={styles.strong}>{selectedAffiliated.nomJeuneFille}</Box>}
                                        {selectedAffiliated.dtAnniversaire && (
                                            <Typography>
                                                {t('affiliations.affiliates.ageAndBirthdate', {
                                                    birthdate: moment(selectedAffiliated.dtAnniversaire, 'YYYY-MM-DD').format('DD/MM/YYYY'),
                                                    age: moment().diff(selectedAffiliated.dtAnniversaire, 'years'),
                                                })}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box className={styles.summary__societyInfo}>
                                        {selectedAffiliated.nomSouscripteur && <Box className={classnames('mb-2', styles.risk)}>{selectedAffiliated.nomSouscripteur}</Box>}
                                        {selectedAffiliated.matriculeEntreprise && (
                                            <Typography>
                                                {t('affiliations.affiliates.matricule')} : <span className={styles.strong}>{selectedAffiliated.matriculeEntreprise}</span>
                                            </Typography>
                                        )}
                                        {selectedAffiliated.categorie && (
                                            <Typography>
                                                {t('affiliations.affiliates.csp')} : <span className={styles.strong}>{selectedAffiliated.categorie}</span>
                                            </Typography>
                                        )}
                                        <Typography>
                                            <FontAwesomeIcon icon={faCalendarAlt} /> {t('affiliations.affiliates.hireDate')} :{' '}
                                            <span className={styles.strong}>
                                                {selectedAffiliated.dtEmbauche ? moment(selectedAffiliated.dtEmbauche, 'YYYY-MM-DD').format('DD/MM/YYYY') : 'Valeur non renseignée'}
                                            </span>
                                        </Typography>
                                    </Box>
                                </Box>
                                <SGSTabs
                                    value={selectedTab}
                                    onChange={handleSelectedTab}
                                    TabIndicatorProps={{
                                        style: { height: '0px', top: '0px' },
                                    }}
                                >
                                    {hasRole(user, ['ROLE_VALID_ASSURED']) && (
                                        <SGSTab backgroundColor={styles.contratBackground} label="CONTRATS EN COURS" count={getActiveContrats(contrats).length}></SGSTab>
                                    )}
                                    {hasRole(user, ['ROLE_HISTORY_ASSURED']) && (
                                        <SGSTab backgroundColor={styles.contratBackground} label="CONTRATS INACTIFS" count={getInactiveContrats(contrats).length}></SGSTab>
                                    )}
                                </SGSTabs>
                                <SGSTabPanel value={selectedTab} index={0} className={styles.contratWrapper}>
                                    {getContrats(getActiveContrats(contrats), t, isOnGoingCancellation)}
                                </SGSTabPanel>
                                <SGSTabPanel value={selectedTab} index={1} className={styles.contratWrapper}>
                                    {getContrats(getInactiveContrats(contrats), t, isOnGoingCancellation)}
                                </SGSTabPanel>
                            </Box>
                        </>
                    )}
                    {contratsLoading && (
                        <Box className={styles.loading}>
                            <Loader loading={contratsLoading} />
                        </Box>
                    )}
                </SGSDrawer>
            </Box>

            <SGSModal isOpenModal={isModalOpen} setIsOpenModal={setIsModalOpen} title={'Radier un salarié'} displayCloseBtn={true} CloseBtnText={'ANNULER'}>
                <Box className={styles.modalContent}>
                    <Box className={styles.modalContent__title}>Veuillez sélectionner un souscripteur</Box>
                    <List className={styles.modalContent__list}>
                        {souscripChoise.map((s) => (
                            <ListItem button key={s.souscrip} onClick={() => handleSouscripSelect(s.souscripteurCod)}>
                                <ListItemText primary={s.souscrip} />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </SGSModal>
        </>
    );
};

export default Affiliates;

function getStatusIcon(value: string, faSize: SizeProp = 'lg', padding: string = '4', text?: string) {
    let icon = null;

    switch (value) {
        case '3': // aniste
            icon = (
                <span className={classnames('fa-layers fa-fw', `fa-${faSize}`, `p-${padding}`, styles.anisteIcon)}>
                    <FontAwesomeIcon icon={faFile} />
                    <FontAwesomeIcon icon={faCheckCircle} transform="shrink-8 down-4 right-7" stroke="white" strokeWidth="15" />
                </span>
            );
            if (text) {
                return (
                    <Box className={styles.affiliateIconBackground} display="flex" alignItems="center">
                        {icon}
                        <Typography className="ml-1">{text}</Typography>
                    </Box>
                );
            }
            break;
        case '1': // actif
            icon = (
                <span className={classnames('fa-layers fa-fw', `fa-${faSize}`, `p-${padding}`, styles.affiliateIcon)}>
                    <FontAwesomeIcon icon={faFile} />
                    <FontAwesomeIcon icon={faCheckCircle} transform="shrink-8 down-4 right-7" stroke="white" strokeWidth="15" />
                </span>
            );
            if (text) {
                return (
                    <Box className={styles.affiliateIconBackground} display="flex" alignItems="center">
                        {icon}
                        <Typography className="ml-1">{text}</Typography>
                    </Box>
                );
            }
            break;
        case '0': // radié
            icon = (
                <span className={classnames('fa-layers fa-fw', `fa-${faSize}`, `p-${padding}`, styles.biaIcon)}>
                    <FontAwesomeIcon icon={faFile} />
                    <FontAwesomeIcon icon={faTimesCircle} transform="shrink-8 down-4 right-3" stroke="white" strokeWidth="15" />
                </span>
            );
            if (text) {
                return (
                    <Box className={styles.affiliateIconBackground} display="flex" alignItems="center">
                        {icon}
                        <Typography className="ml-1">{text}</Typography>
                    </Box>
                );
            }
            break;

        case '5': // Cancellation in process
            icon = (
                <span className={classnames('fa-layers fa-fw', `fa-${faSize}`, `p-${padding}`, styles.cancellationIcon)}>
                    <FontAwesomeIcon icon={faHourglassHalf} />
                </span>
            );
            if (text) {
                return (
                    <Box className={styles.biaIconBackground} display="flex" alignItems="center">
                        {icon}
                        <Typography className="ml-1">{text}</Typography>
                    </Box>
                );
            }
            break;
        // TODO :: update status to dissociate BIA
        case '6': // BIA
            icon = (
                <span className={classnames('fa-layers fa-fw', `fa-${faSize}`, `p-${padding}`, styles.biaIcon)}>
                    <FontAwesomeIcon icon={faHourglassHalf} />
                </span>
            );
            if (text) {
                return (
                    <Box className={styles.biaIconBackground} display="flex" alignItems="center">
                        {icon}
                        <Typography className="ml-1">{text}</Typography>
                    </Box>
                );
            }
            break;
        case '7': // Erreur
            icon = (
                <span className={classnames('fa-layers fa-fw', `fa-${faSize}`, `p-${padding}`, styles.biaIcon)}>
                    <FontAwesomeIcon icon={faExclamationCircle} />
                </span>
            );
            if (text) {
                return (
                    <Box className={styles.biaIconBackground} display="flex" alignItems="center">
                        {icon}
                        <Typography className="ml-1">{text}</Typography>
                    </Box>
                );
            }
            break;
        default:
            icon = (
                <span className={classnames('fa-layers fa-fw', `fa-${faSize}`, `p-${padding}`, styles.waitingIcon)}>
                    <FontAwesomeIcon icon={faFileSignature} />
                </span>
            );
            if (text) {
                return (
                    <Box className={styles.waitingIconBackground} display="flex" alignItems="center">
                        {icon}
                        <Typography className="ml-1">{text}</Typography>
                    </Box>
                );
            }
    }
    return icon;
}

function getContrats(contrats: any[], t, isOnGoingCancellation: Function) {
    return contrats.map((contrat: any) => {
        let ayantsDroit = [];
        if (contrat.nbEnfant > 1) {
            ayantsDroit.push(contrat.nbEnfant + ' enfants');
        } else if (contrat.nbEnfant === 1) {
            ayantsDroit.push(contrat.nbEnfant + ' enfant');
        }
        if (contrat.nbConjoint > 1) {
            ayantsDroit.push(contrat.nbConjoint + ' conjoints');
        } else if (contrat.nbConjoint === 1) {
            ayantsDroit.push(contrat.nbConjoint + ' conjoint');
        }
        if (contrat.nbConcubin > 1) {
            ayantsDroit.push(contrat.nbConcubin + ' concubins');
        } else if (contrat.nbConcubin === 1) {
            ayantsDroit.push(contrat.nbConcubin + ' concubin');
        }
        if (contrat.nbAutre > 1) {
            ayantsDroit.push(contrat.nbAutre + ' autres');
        } else if (contrat.nbAutre === 1) {
            ayantsDroit.push(contrat.nbAutre + ' autre');
        }

        const Accordion = withStyles({
            root: {
                border: 'none',
                boxShadow: 'none',
                '&:not(:last-child)': {
                    borderBottom: 0,
                },
                '&:before': {
                    display: 'none',
                },
                '&$expanded': {
                    margin: '0',
                },
                margin: 0,
                padding: 0,
            },
            expanded: {},
        })(MuiAccordion);

        const AccordionSummary = withStyles({
            root: {
                backgroundColor: 'none',
                borderBottom: 'none',
                marginBottom: -1,
                minHeight: 20,
                '&$expanded': {
                    minHeight: 20,
                },
                display: 'inline-flex',
            },
            content: {
                '&$expanded': {
                    margin: '0',
                    padding: '0',
                },
                flexGrow: 0,
            },
            expanded: {},
        })(MuiAccordionSummary);

        const AccordionDetails = withStyles((theme) => ({
            root: {
                padding: theme.spacing(2),
            },
        }))(MuiAccordionDetails);

        return (
            <Box key={contrat.id} className={styles.contrat}>
                <Typography variant="h3">Contrat de {t(getRisk(contrat.type ? contrat.type : 'P'))}</Typography>
                <Divider className={styles.divider}></Divider>
                <Box>
                    {contrat.statut && (
                        <Typography>
                            {'Statut : '}
                            <span className={styles.strong}>
                                {isOnGoingCancellation({ codAffilie: contrat.cod, codSouscrip: contrat.souscripteurCod, codContrat: contrat.codContrat })
                                    ? 'Radiation en cours de traitement...'
                                    : contrat.statut}
                            </span>
                        </Typography>
                    )}
                    {contrat.lbContrat && (
                        <Typography>
                            {t('affiliations.affiliates.contract')} : <span className={styles.strong}>{contrat.lbContrat + ' - ' + (contrat.typeContrat ? contrat.typeContrat : '')}</span>
                        </Typography>
                    )}
                    {contrat.lbCollege && (
                        <Typography>
                            {t('affiliations.affiliates.college')} : <span className={styles.strong}>{contrat.lbCollege}</span>
                        </Typography>
                    )}
                    <Typography>
                        {t('affiliations.affiliates.societyName')} : <span className={styles.strong}>{contrat.nomSouscripteur}</span>
                    </Typography>
                    <Typography>
                        {t('affiliations.affiliates.siret')} : <span className={styles.strong}>{contrat.siret}</span>
                    </Typography>
                    {contrat.options && (
                        <Typography>
                            {'Options : '}
                            <span className={styles.strong}>{contrat.options}</span>
                        </Typography>
                    )}
                    {ayantsDroit.length > 0 && (
                        <Accordion square className="ml-n3 mb-1" style={{ background: 'none', margin: 0, padding: 0 }}>
                            {/* <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" IconButtonProps={{ edge: 'start' }} id="panel1a-header" style={{ height: '24px', flexDirection:'row-reverse' }}> */}
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header" style={{ height: '25px' }}>
                                <Typography>
                                    {'Ayants-droit : '}
                                    <span className={styles.strong}>{ayantsDroit.join(', ') + '.'}</span>
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {contrat.beneficiaires?.length > 0 && (
                                    <TableContainer component={Paper}>
                                        <Table size="small" aria-label="a dense table" className={styles.tableAyantsDroit}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Lien</TableCell>
                                                    <TableCell>Nom</TableCell>
                                                    <TableCell>Prénom</TableCell>
                                                    <TableCell>Né(e) le</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {contrat.beneficiaires.map((benefici: any) => {
                                                    const dateNaissance = format(new Date(benefici.dtNaissance), 'dd/MM/yyyy');
                                                    return (
                                                        <TableRow key={benefici.codBenefici}>
                                                            <TableCell>{benefici.lbLien}</TableCell>
                                                            <TableCell>{benefici.nom}</TableCell>
                                                            <TableCell>{benefici.prenom}</TableCell>
                                                            <TableCell>{dateNaissance}</TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    )}
                    {contrat?.dtBiaAffilie && (
                        <Typography>
                            <Box display="flex" justifyContent="space-between" maxWidth="480px">
                                <Box>
                                    <FontAwesomeIcon icon={faCalendarAlt} /> {t('affiliations.affiliates.dateValidationBiaAffilie')} :
                                </Box>
                                <span className={styles.strong}>{contrat.dtBiaAffilie ? moment(contrat.dtBiaAffilie.date, 'YYYY-MM-DD').format('DD/MM/YYYY') : 'Valeur non renseignée'}</span>
                            </Box>
                        </Typography>
                    )}
                    {contrat?.dtBiaAffilie && contrat?.dtBiaRh && (
                        <Typography>
                            <Box display="flex" justifyContent="space-between" maxWidth="480px">
                                <Box>
                                    <FontAwesomeIcon icon={faCalendarAlt} /> {t('affiliations.affiliates.dateValidationBiaRh')} :
                                </Box>
                                <span className={styles.strong}>{contrat.dtBiaRh ? moment(contrat.dtBiaRh.date, 'YYYY-MM-DD').format('DD/MM/YYYY') : 'Valeur non renseignée'}</span>
                            </Box>
                        </Typography>
                    )}

                    <Typography>
                        <Box display="flex" justifyContent="space-between" maxWidth="480px">
                            <Box>
                                <FontAwesomeIcon icon={faCalendarAlt} /> {t('affiliations.affiliates.souscriptionPeriod')}
                            </Box>
                            <span className={styles.strong}>
                                {contrat.dtDebutValidite ? moment(contrat.dtDebutValidite.date, 'YYYY-MM-DD').format('DD/MM/YYYY') : 'Valeur non renseignée'}
                                {' - '}
                                {contrat.dtFinValidite ? moment(contrat.dtFinValidite.date, 'YYYY-MM-DD').format('DD/MM/YYYY') : 'en cours'}
                            </span>
                        </Box>
                    </Typography>
                    <Typography>
                        <Box display="flex" justifyContent="space-between" maxWidth="480px">
                            <Box>
                                <FontAwesomeIcon icon={faCalendarAlt} /> {t('affiliations.affiliates.affiliationPeriod')}
                            </Box>
                            <span className={styles.strong}>
                                {contrat.dtDebut ? moment(contrat.dtDebut.date, 'YYYY-MM-DD').format('DD/MM/YYYY') : 'Valeur non renseignée'}
                                {' - '}
                                {contrat.dtFin ? moment(contrat.dtFin.date, 'YYYY-MM-DD').format('DD/MM/YYYY') : 'en cours'}
                            </span>
                        </Box>
                    </Typography>
                </Box>
            </Box>
        );
    });
}

function getActiveContrats(contrats: any[]) {
    return contrats.filter((value) => {
        let ret = true;
        if (value.dtFin != null) {
            ret = ret && moment(value.dtFin.date, 'YYYY-MM-DD').isAfter(moment());
        }
        if (value.dtFinValidite != null) {
            ret = ret && moment(value.dtFinValidite.date, 'YYYY-MM-DD').isAfter(moment());
        }
        return ret;
    });
}

function getInactiveContrats(contrats: any[]) {
    return contrats.filter((value) => {
        let ret = false;

        if (value.dtFin != null) {
            ret = ret || !moment(value.dtFin.date, 'YYYY-MM-DD').isAfter(moment());
        }
        if (value.dtFinValidite != null) {
            ret = ret || !moment(value.dtFinValidite.date, 'YYYY-MM-DD').isAfter(moment());
        }
        return ret;
    });
}

function createSortPayload(column, order) {
    const payload = {
        column: column,
        order: order ? 'ASC' : 'DESC',
    };
    const encodedPayload = JSON.stringify(payload);
    const sortParam = `${encodedPayload}`;
    return sortParam;
}
